import {
    AfterViewInit,
    ChangeDetectorRef,
    Component, EventEmitter,
    Input, OnInit,
    Output,
    QueryList,
    ViewChildren
} from '@angular/core';
import {BehaviorSubject, combineLatestWith} from 'rxjs';
import { map} from 'rxjs/operators';
import * as R from 'ramda';
import {DatePipe} from '@angular/common';
import {AttributeGroup} from '@emm/core/models/attribute-group.model';
import {SharedFacadeService} from '@emm/shared/services/shared-facade.service';
import {Item} from '@emm/core/models/item.model';
import {Locale} from '@emm/core/models/locale.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {ItemsFacadeService} from '@emm/management/services/items-facade.service';
import {ItemGroupingsFacadeService} from '@emm/management/services/item-groupings-facade.service';
import {ItemService} from '@emm/core/services/item.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {
    DynamicManagementFormComponent
} from '@emm/shared/components/dynamic-form/dynamic-management-form/dynamic-management-form.component';

const LEGEND_ITEMS = [{ color: '#B5E9FD', label: 'Global Value' }, { color: '#EBF9FF', label: 'Overridden Value' }];
const DARK_MODE_LEGEND_ITEMS = [{ color: '#4A4A4A', label: 'Global Value' }, { color: '#5B6770', label: 'Overridden Value' }];
const SUBMIT_ORIGIN = 'panel';

@Component({
  selector: 'emm-item-creation-form',
  templateUrl: './item-creation-form.component.html',
  styleUrls: ['./item-creation-form.component.scss']
})
export class ItemCreationFormComponent implements OnInit, AfterViewInit {
    isDarkMode = false;
    attributesByGroup = new Map<string, BehaviorSubject<any>>();
    attributeGroups;
    isReadOnly$ = this.authFacade.isReadOnly$;
    disabled$ = this.isReadOnly$.pipe(combineLatestWith(this.itemsFacade.anyLoading$),
        map((sources: boolean[]) => R.any(R.equals(true), sources))
    );
    form = this.itemsFacade.form$;
    fields = [];
    values = [];
    existingUpdates;
    itemDetail$ = this.itemsFacade.itemDetail$;
    itemTag: string;
    locale: Locale;
    model: any;
    pipe = new DatePipe('en-US');
    renderForm = new BehaviorSubject<boolean>(true);
    selectedStatus: string;
    @Input() attributes: any;
    itemTypes = [];
    @Input() previousAttributeGroups: AttributeGroup[];
    @Output() attributeGroupSelected = new EventEmitter<AttributeGroup[]>();
    @Output() cancelClicked = new EventEmitter<void>();
    @Output() submissionFinished = new EventEmitter<void>();
    @ViewChildren(DynamicManagementFormComponent) dynamicForms: QueryList<DynamicManagementFormComponent>;

    constructor(
        private authFacade: AuthFacadeService,
        private itemsFacade: ItemsFacadeService,
        private itemGroupingsFacade: ItemGroupingsFacadeService,
        private itemService: ItemService,
        private notificationService: NotificationService,
        public sharedFacade: SharedFacadeService,
        private changeDetector: ChangeDetectorRef,
        private api: ManagementApiService,
    ) { }



    ngOnInit(): void {
        this.sharedFacade.currentTheme$.subscribe(theme => {
            theme === 'dark-theme' ? this.isDarkMode = true : this.isDarkMode = false;
        });
        this.getItemTypes().then(() => {setTimeout(() => {this.getAttributeGroups()}, 500)});
        if (this.itemGroupingsFacade.itemDetail$.value !== null) {
            this.itemDetail$ = this.itemGroupingsFacade.itemDetail$;
        }
    }


    ngAfterViewInit() {
        this.dynamicForms.changes.subscribe(() => {
            this.changeDetector.detectChanges();
        });
    }

    cancel(): void {
        this.cancelClicked.next();
    }

    hasChanges(): boolean {
        let result = false;
        try {
            if (this.renderForm && this.dynamicForms) {
                this.dynamicForms.forEach(df => {
                    if (df.form.dirty) {
                        result = true;
                    }
                });
            }
        } catch (e) {}
        return result;
    }

    async destroyForm() {
        await this.renderForm.next(false);
    }

    initializeLocaleForm() {
        this.renderForm.next(true);
    }

    async getItemTypes() {
        if (!window.location.toString().includes('groupings')) {
            await this.api.getAllItemTypesForItems().subscribe(itemTypes => {
                this.itemTypes = itemTypes;
            });
        } else {
            await this.api.getAllItemTypesForItemGroups().subscribe(itemTypes => {
                this.itemTypes = itemTypes;
            });
        }
    }

    getAttributeGroups(): void {
        this.buildAttributes();
        this.api.getAllAttributeGroups().subscribe((res) => {
            this.attributeGroups = res;
            this.attributeGroups[0] = {id: 'REQUIRED', name: 'Required Attributes'};
            res.forEach(group => {
                this.attributesByGroup.set(group.id, this.filterByAttributeGroup(group.id))
            });
        });
    }

    filterByAttributeGroup(filterAttributeGroup: string) {
        const filterByGroup$ = new BehaviorSubject<any>(null);
        for (const attribute in this.attributes) {
            if (this.attributes[attribute].required) {
                this.attributes[attribute].attributeGroup = 'REQUIRED';
            }
        }
        filterByGroup$.next(this.attributes.filter(attribute => attribute.attributeGroup === filterAttributeGroup));
        return filterByGroup$
    }


    hasValue(attributeGroupId: string): boolean {
        const attributesByGroup = this.attributesByGroup.get(attributeGroupId)
        return attributesByGroup?.getValue().length !== 0
    }


    openPanel(status): void {
        this.selectedStatus = status;
        try {
            if (!this.isPreviousPanel(status)) {
                this.previousAttributeGroups.push(status);
            }
            this.attributeGroupSelected.next(this.previousAttributeGroups);
        } catch (e) {}
    }


    closePanel(status): void {
        this.selectedStatus = null;
        this.previousAttributeGroups.find((item, index) => item === status && this.previousAttributeGroups.splice(index, 1));
        this.attributeGroupSelected.next(this.previousAttributeGroups);
    }

    isPreviousPanel(status) {
        try {
            return this.previousAttributeGroups.some(i => i.name === status.name);
        } catch (e) {
            return false;
        }
    }

    buildAttributes(): void {
        const tagAttribute = {
            attributeGroup: 'REQUIRED',
            dateUpdated: new Date().toISOString(),
            tag: 'TAG',
            name: 'Tag',
            jsonProperty: 'tag',
            description: 'Unique item identifier',
            options: null,
            type: 'TEXT',
            required: true,
            scope: 'GLOBAL',
            label: 'Tag'
        };
        this.attributes.push(tagAttribute);
        const typeAttribute = {
            attributeGroup: 'REQUIRED',
            dateUpdated: new Date().toISOString(),
            tag: 'TYPE',
            name: 'Type',
            jsonProperty: 'type',
            description: 'Item type',
            options: [],
            type: 'SELECT',
            required: true,
            scope: 'GLOBAL',
            label: 'Type'
        };
        for (const itemType of this.itemTypes) {
            typeAttribute.options.push({label: itemType.name, value: itemType.tag});
        }
        this.attributes.push(typeAttribute);
        const lockedttribute = {
            attributeGroup: 'REQUIRED',
            dateUpdated: new Date().toISOString(),
            tag: 'LOCKED',
            name: 'Locked',
            jsonProperty: 'locked',
            description: 'Unique item identifier',
            options: [{label: 'True', value: true}, {label: 'False', value: false}],
            type: 'SELECT',
            required: true,
            scope: 'GLOBAL',
            label: 'Locked'
        };
        this.attributes.push(lockedttribute);
        this.orderAttributes();
    }

    orderAttributes() {
        let orderedRequiredAttributes = [];
        for (const attribute of this.attributes) {
            switch (attribute.tag) {
                case 'NAME':
                    orderedRequiredAttributes[0] = attribute;
                    break;
                case 'TAG':
                    orderedRequiredAttributes[1] = attribute;
                    break;
                case 'TYPE':
                    orderedRequiredAttributes[2] = attribute;
                    break;
                case 'LOCKED':
                    orderedRequiredAttributes[3] = attribute;
                    break;
                case 'DAY_PART':
                    orderedRequiredAttributes[4] = attribute;
                    break;
                case 'DISABLED':
                    orderedRequiredAttributes[5] = attribute;
                    break;
                case 'MEAL':
                    orderedRequiredAttributes[6] = attribute;
                    break;
                case 'ALLOW_SPECIAL_INSTRUCT':
                    orderedRequiredAttributes[7] = attribute;
                    break;
                case 'DOT_COM_VISIBLE':
                    orderedRequiredAttributes[8] = attribute;
                    break;
                case 'ITEM_GROUP_TYPE':
                    orderedRequiredAttributes[9] = attribute;
                    break;
                case 'STATUS':
                    orderedRequiredAttributes[10] = attribute;
                    break;
                case 'POS_NUTRITION_CATEGORY':
                    orderedRequiredAttributes[11] = attribute;
                    break;
                case 'SALT_LAW':
                    orderedRequiredAttributes[12] = attribute;
                    break;
            }
        }
        const nonRequiredAttributes = this.attributes.filter(attribute => attribute.required !== true);
        orderedRequiredAttributes = orderedRequiredAttributes.concat(nonRequiredAttributes);
        this.attributes = orderedRequiredAttributes;
    }

    getDirtyValues() {
        const dirtyValues = [];
        this.dynamicForms.forEach((df) => {
            if (df.form && df.form.controls) {
                Object.entries(df.form.controls).forEach(([name, control]) => {
                    control.markAsDirty();
                    for (const attribute of this.attributes) {
                        if (attribute.jsonProperty === name) {
                            control['tag'] = attribute.tag;
                        }
                    }

                    if (control.dirty && !dirtyValues.some((value) => value.tag === control['tag'])) {
                        dirtyValues.push({tag: control['tag'], value: control.value})
                    }
                });
            }
        });
        if (!dirtyValues.some((value) => value.tag === 'NAME')) {
            dirtyValues.push({tag: 'NAME', value: this.dynamicForms.first.form.controls['name'].value});
        }
        return dirtyValues;
    }

    getAllDirtyAttributes() {
        let mergedUpdates;
        let newUpdates;

        newUpdates = {...newUpdates, ...this.getDirtyValues()}
        if (this.existingUpdates !== undefined) {
            mergedUpdates = {...mergedUpdates, ...newUpdates, ...this.existingUpdates};
            this.existingUpdates = null;
        } else {
            mergedUpdates = {...mergedUpdates, ...newUpdates};
        }

        Object.keys(mergedUpdates).forEach((key) => {
            if (typeof (mergedUpdates === undefined ? newUpdates[key] === 'string' : mergedUpdates[key]) === 'string') {
                if (mergedUpdates === undefined) {
                    newUpdates[key] = newUpdates[key].trim();
                } else {
                    mergedUpdates[key] = mergedUpdates[key].trim();
                }
            }
        });
        return mergedUpdates === undefined ? newUpdates : mergedUpdates;
    }

    submit(reloadAfterUpdate: boolean = true): void {
        if (this.dynamicForms.first.form.invalid || this.hasInvalidSubsGroupId()) {
            if (this.hasInvalidSubsGroupId()) {
                this.notificationService.error(`If an item has an RMI, it must also have a Subs Group ID`);
                return;
            }
            this.notificationService.error(`Please fill out all required fields.`);
            return;
        }
        let mergedUpdates = {...this.getAllDirtyAttributes()};
        mergedUpdates = Object.values(mergedUpdates).filter(value => value['tag'] !== 'TAG' &&
            value['tag'] !== 'TYPE' && value['tag'] !== 'LOCKED');
        const mergedUpdatesArray = [];
        Object.values(mergedUpdates).forEach((value) => mergedUpdatesArray.push(value));
        this.itemService.saveItem({
            tag: this.dynamicForms.first.form.controls['tag'].value,
            itemType: this.dynamicForms.first.form.controls['type'].value,
            locked: false,
            attributes: mergedUpdatesArray,
        }).subscribe({
                next: (res) => {
                    this.notificationService.success(`New item successfully created`);
                    this.submissionFinished.next();
                },
                error: (err) => {
                    const error = JSON.parse(err.error);
                    this.notificationService.error(error?.error?.message || API_ERRORS.GenericErrorMessage);
                }
            }
        );
    }

    imageValidation(defaultValue): void {
        if (this.dynamicForms) {
            const imageForm = this.dynamicForms?.find((df) =>
                df?.attributeGroupId === 'IMAGE');

            const pattern = new RegExp(
                '^https?:\\/\\/' +                              // Match http:// or https://
                '[-a-zA-Z0-9@:%._\\+~#=]{2,256}' +              // Strict domain segment
                '\\.[a-z]{2,6}\\b' +                            // Top-level domain (e.g., .com, .net)
                '([-a-zA-Z0-9@:%_\\+.~#?&/=]*)' +               // Optional path
                '\\.(webp|png|jpg|jpeg|gif)' +                      // File extension check
                '(\\?.*)?(#.*)?$',                              // Optional query and fragment
                'i'                                             // Case-insensitive
            );

            if (pattern.test(imageForm.form?.controls[defaultValue].value)) {
                imageForm.form?.controls[defaultValue].setErrors(null);
                return;
            }

            imageForm.form?.controls[defaultValue].setErrors({ pattern: true });
            this.notificationService.error('Invalid URL format');
        }
    }

    hasInvalidImageLink(): boolean {
        let result = false;
        if (this.renderForm && this.dynamicForms) {
            const imageForm = this.dynamicForms?.find((df) =>
                df?.attributeGroupId === 'IMAGE');
            result = imageForm?.form?.invalid;
        }
        return result;
    }

    hasInvalidSubsGroupId(): boolean {
        let result = false;
        if (this.renderForm && this.dynamicForms) {
            const coreForm = this.dynamicForms?.find((df) =>
                df?.attributeGroupId === 'CORE');
            result = coreForm?.form?.invalid;
        }
        return result;
    }

    hasInvalidFormfield() {
        return !!(this.hasInvalidImageLink() || this.hasInvalidSubsGroupId());
    }
}
